import { SalaryPreferences } from './SalaryPreferences';
import { validCompensationRange } from '../utils/remuneration';
// Formulaire principal de candidature.
// Responsable uniquement de l'UI et de relayer les changements et la soumission.
// Utilisation:
// <ApplicationForm form={form} errors={errors} update={update} onSubmit={handleSubmit} submitting={submitting} t={t} />
import React from 'react';
import { ResumePrefillStep } from './ResumePrefillStep';
import { backendUrl, siteUrl } from '../config_prod';
import { FormLanguage, FormState } from '../type/form-step';
import { emailValid, phoneValid, sanitizePhone } from '../utils/get-query-params';
import { isImageFile, isPdfFile } from '../utils/file-validation';

type Props = {
  opportunityId: string;
  onPrefill: (values: Partial<FormState>) => void;
  form: FormState;
  errors: Record<string, string>;
  setErrors: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  update: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  onSubmit: (e: React.FormEvent) => void;
  submitting: boolean;
  verificationRequired: boolean;
  verificationMessage: string | null;
  verificationSuccess: boolean;
  verificationPolling: boolean;
  verificationResending: boolean;
  verificationCode: string;
  verificationVerifying: boolean;
  applicationConfirmed: boolean;
  applicationSending: boolean;
  submitDisabled?: boolean;
  onResendCode: () => void;
  onVerificationCodeChange: (value: string) => void;
  onVerifyCode: () => void;
  t: Record<string, string>;
  debugFormUrl?: string | null; // affichage optionnel de l'URL ATS courante
};

export function ApplicationForm({
  form,
  opportunityId,
  onPrefill,
  errors,
  setErrors,
  update,
  onSubmit,
  submitting,
  verificationRequired,
  verificationMessage,
  verificationSuccess,
  verificationPolling,
  verificationResending,
  verificationCode,
  verificationVerifying,
  applicationConfirmed,
  applicationSending,
  submitDisabled = false,
  onResendCode,
  onVerificationCodeChange,
  onVerifyCode,
  t,
  debugFormUrl,
}: Props) {
  const [prefillBusy, setPrefillBusy] = React.useState(false);
  const [resumePrefilled, setResumePrefilled] = React.useState(false);
  const cguUrl = `${siteUrl}/${form.lang}/general-conditions/cgu`;
  const cguText = form.lang === 'fr' ? 'CGU' : 'Terms';
  const [acceptBefore, acceptAfter] = (t.accept || '').split(cguText);
  const contractTypes = ['cdi', 'cdd', 'freelance', 'interim', 'stage', 'alternant', 'vie', 'civic_service', 'help_contract', 'partial_time'];
  const workTypes = ['non', 'hybrid', 'full'];
  const languageLevels = [
    { value: 'native', label: t.languageNative },
    { value: 'fluent', label: t.languageFluent },
    { value: 'intermediate', label: t.languageIntermediate },
    { value: 'basic', label: t.languageBasic },
  ];
  const [languageSearch, setLanguageSearch] = React.useState('');
  const [languageSuggestions, setLanguageSuggestions] = React.useState<FormLanguage[]>([]);
  const [languageSuggestionsOpen, setLanguageSuggestionsOpen] = React.useState(false);
  const profileImagePreviewUrl = React.useMemo(
    () => (form.profileImage ? URL.createObjectURL(form.profileImage) : ''),
    [form.profileImage],
  );
  React.useEffect(() => {
    return () => {
      if (profileImagePreviewUrl) URL.revokeObjectURL(profileImagePreviewUrl);
    };
  }, [profileImagePreviewUrl]);
  const toggleContractType = (contractType: string) => {
    update(
      'contractType',
      form.contractType.includes(contractType)
        ? form.contractType.filter((value) => value !== contractType)
        : [...form.contractType, contractType],
    );
  };
  const toggleWorkType = (workType: string) => {
    update(
      'workType',
      form.workType.includes(workType)
        ? form.workType.filter((value) => value !== workType)
        : [...form.workType, workType],
    );
  };
  const fetchLanguageSuggestions = async (term: string) => {
    setLanguageSearch(term);
    if (term.trim().length < 2) {
      setLanguageSuggestions([]);
      setLanguageSuggestionsOpen(false);
      return;
    }

    try {
      const response = await fetch(
        `${backendUrl}/kscan/v1/languages/get/autocomplete?lang=${form.lang}&q=${encodeURIComponent(term)}&top=7`,
      ).then((res) => res.json());

      const suggestions = Array.isArray(response?.data)
        ? response.data.map((item: { code: string; name_en: string; name_fr: string }) => ({
            code: item.code,
            name_en: item.name_en,
            name_fr: item.name_fr,
            level: 'basic',
          }))
        : [];

      setLanguageSuggestions(suggestions);
      setLanguageSuggestionsOpen(suggestions.length > 0);
    } catch {
      setLanguageSuggestions([]);
      setLanguageSuggestionsOpen(false);
    }
  };
  const addLanguage = (language: FormLanguage) => {
    if (form.languages.some((existing) => existing.code.toLowerCase() === language.code.toLowerCase())) {
      setLanguageSearch('');
      setLanguageSuggestions([]);
      setLanguageSuggestionsOpen(false);
      return;
    }
    update('languages', [...form.languages, { ...language, level: form.languageLevel || 'fluent' }]);
    setLanguageSearch('');
    setLanguageSuggestions([]);
    setLanguageSuggestionsOpen(false);
  };
  const updateLanguageLevel = (code: string, level: string) => {
    update(
      'languages',
      form.languages.map((language) => (language.code === code ? { ...language, level } : language)),
    );
  };
  const deleteLanguage = (code: string) => {
    update('languages', form.languages.filter((language) => language.code !== code));
  };
  const languageNameKey = `name_${form.lang}` as 'name_en' | 'name_fr';
  const [currentStep, setCurrentStep] = React.useState(0);
  const updateNamePart = (key: 'firstName' | 'lastName', value: string) => {
    const firstName = key === 'firstName' ? value : form.firstName;
    const lastName = key === 'lastName' ? value : form.lastName;
    update(key, value);
    update('name', `${firstName.trim()} ${lastName.trim()}`.trim());
  };
  const stepDefinitions = React.useMemo(
    () => [
      { id: 'resume', label: t.resume, fields: ['resume'] },
      {
        id: 'personal',
        label: t.stepPersonal || (form.lang === 'fr' ? 'Informations personnelles' : 'Personal information'),
        fields: ['firstName', 'lastName', 'role', 'email', 'phone', 'profileImage'],
      },
      {
        id: 'profile',
        label: t.stepProfile || t.profileDetails,
        fields: ['languages'],
      },
      {
        id: 'preferences',
        label: t.stepPreferences || t.preferences,
        fields: ['rayonKm', 'salary'],
      },
      {
        id: 'terms',
        label: t.stepTerms || (form.lang === 'fr' ? 'Accepter les CGU' : 'Accept terms'),
        fields: ['acceptCGU'],
      },
      {
        id: 'verify',
        label: t.stepVerifyEmail || (form.lang === 'fr' ? "Vérifier l'email" : 'Verify email'),
        fields: ['verification'],
      },
      {
        id: 'confirm',
        label: t.stepConfirmApplication || (form.lang === 'fr' ? 'Confirmer la postulation' : 'Confirm application'),
        fields: [],
      },
    ],
    [form.lang, t],
  );
  const lastStepIndex = stepDefinitions.length - 1;
  const validateStep = (stepIndex: number): boolean => {
    const stepErrors: Record<string, string> = {};

    if (stepIndex === 0 && !form.resume) stepErrors.resume = t.required;
    if (stepIndex === 2 && form.languages.some((language) => !language.level)) stepErrors.languages = t.required;
    if (stepIndex === 1) {
      if (!form.firstName.trim()) stepErrors.firstName = t.required;
      if (!form.lastName.trim()) stepErrors.lastName = t.required;
      if (!form.role.trim()) stepErrors.role = t.required;
      if (!form.email.trim()) stepErrors.email = t.required;
      else if (!emailValid(form.email)) stepErrors.email = t.invalidEmail;
      if (!form.phone.trim()) stepErrors.phone = t.required;
      else if (!phoneValid(form.phone)) stepErrors.phone = t.invalidPhone;
      if (!form.resume) stepErrors.resume = t.required;
      else if (!isPdfFile(form.resume)) stepErrors.resume = t.invalidResumePdf;
      if (form.profileImage && !isImageFile(form.profileImage)) stepErrors.profileImage = t.invalidProfileImage;
      if (form.profileImage && form.profileImage.size > 5 * 1024 * 1024) stepErrors.profileImage = t.invalidProfileImageSize;
    }

    if (stepIndex === 3) {
      if (form.rayonKm && (!Number.isFinite(Number(form.rayonKm)) || Number(form.rayonKm) < 0)) stepErrors.rayonKm = t.invalidRadius;
      if (!validCompensationRange(form.minSalary, form.maxSalary) || !validCompensationRange(form.dailyRateMin, form.dailyRateMax)) {
        stepErrors.salary = t.invalidSalaryRange;
      }
    }

    if (stepIndex === 4 && !form.acceptCGU) {
      stepErrors.acceptCGU = t.required;
    }

    if (stepIndex === 5 && !verificationSuccess) {
      stepErrors.verification = t.verifyAccountRequired;
    }

    const fieldsToClear = stepDefinitions[stepIndex].fields;
    setErrors((previous) => {
      const nextErrors = { ...previous };
      fieldsToClear.forEach((field) => {
        delete nextErrors[field];
      });
      return { ...nextErrors, ...stepErrors };
    });

    return Object.keys(stepErrors).length === 0;
  };
  const validateStepRange = (fromStep: number, toStep: number): boolean => {
    for (let stepIndex = fromStep; stepIndex < toStep; stepIndex += 1) {
      if (!validateStep(stepIndex)) return false;
    }
    return true;
  };
  const goToStep = (stepIndex: number) => {
    if (prefillBusy) return;
    if (stepIndex <= currentStep || validateStepRange(currentStep, stepIndex)) {
      setCurrentStep(stepIndex);
    }
  };
  const goToNextStep = () => {
    if (prefillBusy) return;
    if (validateStep(currentStep)) {
      setCurrentStep((step) => Math.min(lastStepIndex, step + 1));
    }
  };

  React.useEffect(() => {
    const firstErrorStep = stepDefinitions.findIndex((step) => step.fields.some((field) => Boolean(errors[field])));
    if (firstErrorStep >= 0) setCurrentStep(firstErrorStep);
  }, [errors, stepDefinitions]);

  React.useEffect(() => {
    if (verificationRequired) setCurrentStep(5);
  }, [verificationRequired]);

  React.useEffect(() => {
    if (applicationConfirmed) setCurrentStep(6);
  }, [applicationConfirmed]);

  React.useEffect(() => {
    if (applicationSending) setCurrentStep(6);
  }, [applicationSending]);

  const handleSubmit = (event: React.FormEvent) => {
    if (prefillBusy) { event.preventDefault(); return; }
    setCurrentStep(lastStepIndex);
    onSubmit(event);
  };

  return (
    <form onSubmit={handleSubmit} className="application-step-form" encType="multipart/form-data">
      <input type="hidden" name="source" value={form.source || ''} />
      <div className="application-progress">
        <div><span>{t.stepProgress.replace('{current}', String(currentStep + 1)).replace('{total}', String(stepDefinitions.length))}</span><strong>{stepDefinitions[currentStep].label}</strong></div>
        <progress value={currentStep + 1} max={stepDefinitions.length} aria-label={t.applicationSteps} />
      </div>
      <nav className="application-stepper" aria-label={t.applicationSteps || 'Application steps'}>
        {stepDefinitions.map((step, index) => {
          const isActive = index === currentStep;
          const isComplete = index < currentStep;
          const hasError = step.fields.some((field) => Boolean(errors[field]));
          return (
            <button
              key={step.id}
              type="button"
              className={`application-stepper-item${isActive ? ' active' : ''}${isComplete ? ' complete' : ''}${hasError ? ' has-error' : ''}`}
              aria-current={isActive ? "step" : undefined}
              disabled={prefillBusy || submitting}
              onClick={() => goToStep(index)}
            >
              <span className="application-stepper-index" aria-hidden="true">{isComplete ? "✓" : index + 1}</span>
              <span className="application-stepper-label">{step.label}</span>
            </button>
          );
        })}
      </nav>

      {currentStep === 0 && <ResumePrefillStep onContinue={() => { if (validateStep(0)) setCurrentStep(1); }} error={errors.resume} form={form} update={update} onPrefill={(values) => { onPrefill(values); setResumePrefilled(true); }} opportunityId={opportunityId} onBusy={setPrefillBusy} t={t} />}
      {currentStep === 1 && (
        <section className="form-grid application-step-panel" aria-labelledby="step-personal-title">
          <h3 id="step-personal-title" className="step-panel-title">{stepDefinitions[1].label}</h3>
          {resumePrefilled && <p role="status" className="cv-feedback is-success" style={{gridColumn: "1 / -1"}}>{t.cvPrefilled}</p>}
          <div className="field profile-image-field">
            <label>{t.profileImage}</label>
            <div className="profile-image-row">
              <div className="profile-image-preview" aria-hidden="true">
                {profileImagePreviewUrl ? (
                  <img src={profileImagePreviewUrl} alt="" />
                ) : (
                  <span>{(form.firstName || form.lastName || form.name).trim().charAt(0).toUpperCase() || '?'}</span>
                )}
              </div>
              <label className="button ghost file-button">
                {t.chooseProfileImage}
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={(e) => {
                    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    if (file && !isImageFile(file)) {
                      e.target.value = '';
                      update('profileImage', null);
                      return;
                    }
                    update('profileImage', file);
                  }}
                />
              </label>
            </div>
            {form.profileImage && <div className="file-info">{form.profileImage.name}</div>}
            {errors.profileImage && <div className="error">{errors.profileImage}</div>}
          </div>

          <div className="field">
            <label>{t.firstName}</label>
            <input
              type="text"
              value={form.firstName}
              onChange={(e) => updateNamePart('firstName', e.target.value)}
              placeholder={t.firstName}
              autoComplete="given-name"
            />
            {errors.firstName && <div className="error">{errors.firstName}</div>}
          </div>

          <div className="field">
            <label>{t.lastName}</label>
            <input
              type="text"
              value={form.lastName}
              onChange={(e) => updateNamePart('lastName', e.target.value)}
              placeholder={t.lastName}
              autoComplete="family-name"
            />
            {errors.lastName && <div className="error">{errors.lastName}</div>}
          </div>

          <div className="field">
            <label>{t.role}</label>
            <input type="text" value={form.role} onChange={(e) => update('role', e.target.value)} placeholder={t.role} autoComplete="organization-title" />
            {errors.role && <div className="error">{errors.role}</div>}
          </div>

          <div className="field">
            <label>{t.email}</label>
            <input type="email" value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="name@example.com" autoComplete="email" />
            {errors.email && <div className="error">{errors.email}</div>}
          </div>

          <div className="field">
            <label>{t.phone}</label>
            <input
              type="tel"
              inputMode="tel"
              value={form.phone}
              onChange={(e) => update('phone', sanitizePhone(e.target.value))}
              placeholder="+33612345678"
              maxLength={16}
              autoComplete="tel"
            />
            {errors.phone && <div className="error">{errors.phone}</div>}
          </div>

          <div className="field">
            <label>{t.address}</label>
            <input
              type="text"
              value={form.address}
              onChange={(e) => update('address', e.target.value)}
              placeholder={t.addressPlaceholder}
              autoComplete="street-address"
            />
          </div>


        </section>
      )}

      {currentStep === 2 && (
        <fieldset className="preferences-section application-step-panel">
          <legend>{t.profileDetails}</legend>
        <div className="field">
          <label>{t.schoolLevel}</label>
          <select value={form.schoolLevel} onChange={(e) => update('schoolLevel', e.target.value)}>
            <option value="">{t.notSpecified}</option>
            <option value="NO_DEGREE">{t.schoolLevelNoDegree}</option>
            <option value="BAC">Bac</option>
            <option value="BAC+2_3">Bac+2/3 | Bachelor</option>
            <option value="BAC+4_5">Bac+4/5 | Master</option>
            <option value="DOCTORAT">Doctorat | PhD</option>
          </select>
        </div>

        <div className="field">
          <label>{t.experience}</label>
          <select value={form.experienceMonths} onChange={(e) => update('experienceMonths', e.target.value)}>
            <option value="">{t.notSpecified}</option>
            <option value="-1">0-2 {t.years}</option>
            <option value="24">2-3 {t.years}</option>
            <option value="36">3-5 {t.years}</option>
            <option value="60">5-10 {t.years}</option>
            <option value="120">10+ {t.years}</option>
          </select>
        </div>

        <div className="field">
          <label>{t.availability}</label>
          <select value={form.availableDate} onChange={(e) => update('availableDate', e.target.value)}>
            <option value="NOW">{t.availabilityNow}</option>
            <option value="LESS_THAN_1_MONTH">{t.availabilityNotice}</option>
            <option value="1_TO_3_MONTHS">{t.availabilityNegotiable}</option>
          </select>
        </div>

        <div className="field language-field">
          <label>{t.languageKnown}</label>
          <div className="language-search-row">
            <input
              type="text"
              value={languageSearch}
              onChange={(e) => void fetchLanguageSuggestions(e.target.value)}
              placeholder={t.languageSearchPlaceholder}
              autoComplete="off"
            />
            <select value={form.languageLevel} onChange={(e) => update('languageLevel', e.target.value)}>
              {languageLevels.map((level) => (
                <option key={level.value} value={level.value}>
                  {level.label}
                </option>
              ))}
            </select>
          </div>
          {languageSuggestionsOpen && (
            <div className="language-suggestions">
              {languageSuggestions.map((suggestion) => (
                <button
                  key={suggestion.code}
                  type="button"
                  className="language-suggestion"
                  onClick={() => addLanguage(suggestion)}
                >
                  {suggestion[languageNameKey] || suggestion.name_en}
                </button>
              ))}
            </div>
          )}
          {errors.languages && <div className="error">{errors.languages}</div>}
          <div className="language-list">
            {form.languages.length === 0 ? (
              <div className="file-info">{t.noLanguage}</div>
            ) : (
              form.languages.map((language) => (
                <div key={language.code} className="language-item">
                  <span>{language[languageNameKey] || language.name_en}</span>
                  <select value={language.level} onChange={(e) => updateLanguageLevel(language.code, e.target.value)}>
                    <option value="">{t.notSpecified}</option>
                    {languageLevels.map((level) => (
                      <option key={level.value} value={level.value}>
                        {level.label}
                      </option>
                    ))}
                  </select>
                  <button type="button" className="language-remove" onClick={() => deleteLanguage(language.code)}>
                    x
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
        </fieldset>
      )}

      {currentStep === 3 && (
        <fieldset className="preferences-section application-step-panel">
          <legend>{t.preferences}</legend>
        <div className="field checkbox compact">
          <label>
            <input type="checkbox" checked={form.mobile} onChange={(e) => update('mobile', e.target.checked)} />
            <span>{t.mobility}</span>
          </label>
        </div>

        <div className="field">
          <label>{t.city}</label>
          <input type="text" value={form.city} onChange={(e) => update('city', e.target.value)} placeholder={t.cityPlaceholder} />
        </div>

        <div className="field">
          <label>{t.radius}</label>
          <input type="number" min="0" value={form.rayonKm} onChange={(e) => update('rayonKm', e.target.value)} placeholder="50" />
          {errors.rayonKm && <div className="error">{errors.rayonKm}</div>}
        </div>

        <div className="field">
          <label>{t.workMode}</label>
          <div className="chip-grid">
            {workTypes.map((workType) => (
              <button
                key={workType}
                type="button"
                className={`chip${form.workType.includes(workType) ? ' selected' : ''}`}
                onClick={() => toggleWorkType(workType)}
              >
                {t[`workMode.${workType}`] || workType}
              </button>
            ))}
          </div>
        </div>

        <div className="field contract-field">
          <label>{t.contractTypes}</label>
          <div className="chip-grid">
            {contractTypes.map((contractType) => (
              <button
                key={contractType}
                type="button"
                className={`chip${form.contractType.includes(contractType) ? ' selected' : ''}`}
                onClick={() => toggleContractType(contractType)}
              >
                {t[`contractType.${contractType}`] || contractType}
              </button>
            ))}
          </div>
        </div>

        <SalaryPreferences form={form} update={update} />

        {errors.salary && <div className="error preferences-error">{errors.salary}</div>}
        </fieldset>
      )}

      {currentStep === 4 && (
        <section className="application-step-panel single-step-panel">
          <h3 className="step-panel-title">{stepDefinitions[4].label}</h3>
          <div className="field checkbox">
            <label>
              <input type="checkbox" checked={form.acceptCGU} onChange={(e) => update('acceptCGU', e.target.checked)} />
              <span>
                {acceptBefore || (form.lang === 'fr' ? "J'accepte les " : 'I accept the ')}
                <a href={cguUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--muted)', textDecoration: 'underline' }} onClick={(e) => e.stopPropagation()}>
                  {cguText}
                </a>
                {acceptAfter || ''}
              </span>
            </label>
            {errors.acceptCGU && <div className="error">{errors.acceptCGU}</div>}
          </div>
        </section>
      )}

      {currentStep === 5 && (
        <section className="application-step-panel single-step-panel">
          <h3 className="step-panel-title">{stepDefinitions[5].label}</h3>
          <div className={`verification-panel${verificationSuccess ? ' success' : ''}`}>
            <div className="verification-status">
              {(verificationResending || verificationVerifying || verificationPolling) && !verificationSuccess && (
                <span className="verification-spinner" aria-hidden="true" />
              )}
              <div>
                <div className="verification-title">
                  {verificationSuccess ? t.verifyAccountValidated : t.verifyAccountTitle}
                </div>
                <div className="verification-text">
                  {verificationMessage || t.verifyAccountText}
                  {verificationRequired && !verificationSuccess ? ` ${t.verifyAccountEnterCode}` : ''}
                </div>
              </div>
            </div>
            {!verificationSuccess && (
              <>
                <div className="verification-row">
                  <button
                    type="button"
                    className="button ghost cursor-pointer"
                    onClick={onResendCode}
                    disabled={verificationResending || submitting || verificationVerifying}
                  >
                    {verificationResending ? '...' : verificationRequired ? t.verifyAccountResendButton : t.verifyAccountSendCodeButton}
                  </button>
                </div>
                <div className="verification-row">
                  <input
                    type="text"
                    inputMode="text"
                    value={verificationCode}
                    onChange={(event) => onVerificationCodeChange(event.target.value)}
                    placeholder={t.verifyAccountCodePlaceholder}
                    maxLength={6}
                    disabled={!verificationRequired || verificationResending || verificationVerifying}
                  />
                  <button
                    type="button"
                    className="button primary cursor-pointer"
                    onClick={onVerifyCode}
                    disabled={!verificationRequired || verificationCode.trim().length < 6 || verificationVerifying || verificationResending || submitting}
                  >
                    {verificationVerifying ? '...' : t.verifyAccountVerifyCodeButton}
                  </button>
                </div>
                {errors.verification && <div className="error">{errors.verification}</div>}
              </>
            )}
            {verificationSuccess && (
              <button type="button" className="button ghost" disabled>
                {submitting ? t.verifyAccountSubmittingCv : t.verifyAccountValidated}
              </button>
            )}
          </div>
        </section>
      )}

      {currentStep === 6 && (
        <section className="application-step-panel single-step-panel">
          <h3 className="step-panel-title">{stepDefinitions[6].label}</h3>
          {applicationConfirmed ? (
            <div className="verification-panel success">
              <div className="verification-title">{t.applicationConfirmedTitle}</div>
              <div className="verification-text">{t.applicationConfirmedText}</div>
            </div>
          ) : applicationSending ? (
            <div className="verification-panel">
              <div className="verification-status">
                <span className="verification-spinner" aria-hidden="true" />
                <div>
                  <div className="verification-title">{t.applicationSendingTitle}</div>
                  <div className="verification-text">{t.applicationSendingText}</div>
                </div>
              </div>
            </div>
          ) : !verificationRequired && (
            <button type="submit" className="cursor-pointer button primary confirm-application-button" disabled={submitting || submitDisabled}>
              {submitting ? '...' : t.submit}
            </button>
          )}
        </section>
      )}

      <div className={`actions-row step-actions-row${currentStep === 0 ? " first-step-actions" : ""}`}>
        <button
          type="button"
          className="button ghost"
          onClick={() => setCurrentStep((step) => Math.max(0, step - 1))}
          disabled={currentStep === 0 || submitting || prefillBusy}
        >
          {t.previousStep || (form.lang === 'fr' ? 'Précédent' : 'Previous')}
        </button>
        {currentStep < lastStepIndex && (
          <button
            type="button"
            className={`button ${currentStep === 0 ? "ghost" : "primary"}`}
            onClick={goToNextStep}
            disabled={submitting || prefillBusy}
          >
            {t.nextStep || (form.lang === 'fr' ? 'Suivant' : 'Next')}
          </button>
        )}
      </div>
    </form>
  );
}
